import { useMemo } from 'react';
import { javascript } from '@codemirror/lang-javascript';
import { css as cssLanguage } from '@codemirror/lang-css';
import { json as jsonLanguage } from '@codemirror/lang-json';
import { Extension } from '@codemirror/state';
import { Diagnostic, linter } from '@codemirror/lint';
import { getLanguage, extractLocationFromText, normalizePath } from '../utils';
import { LogEntry, LogLocation } from '../types';
import { useEditorState, useLogs } from '../contexts';

type UseEditorExtensionsParams = {
  resolvePath: (id: string) => string;
};

function getLocationFromLog(log: LogEntry): LogLocation | null {
  const fromMessage = extractLocationFromText(log.message);
  if (fromMessage) return fromMessage;
  if (log.details) {
    for (const line of log.details) {
      const found = extractLocationFromText(line);
      if (found) return found;
    }
  }
  return null;
}

export function useEditorExtensions({ resolvePath }: UseEditorExtensionsParams) {
  // Get editor state from context
  const { activeFile } = useEditorState();

  // Get logs from context
  const { logs } = useLogs();
  const lintDiagnostics = useMemo(() => {
    if (!activeFile) return [];
    const activePath = normalizePath(resolvePath(activeFile));
    return logs
      .map((log) => {
        const loc = getLocationFromLog(log);
        if (!loc) return null;
        const target = normalizePath(loc.path);
        if (target !== activePath) return null;
        return {
          line: loc.line,
          column: loc.column,
          message: log.message || 'Lint issue',
          severity: log.level === 'error' ? 'error' : 'warning',
        };
      })
      .filter(Boolean) as { line: number; column: number; message: string; severity: 'error' | 'warning' }[];
  }, [activeFile, logs, resolvePath]);

  const languageExtensions = useMemo<Extension[]>(() => {
    const currentPath = activeFile ? resolvePath(activeFile) : '';
    const lang = getLanguage(currentPath);
    if (lang === 'typescript') return [javascript({ jsx: true, typescript: true })];
    if (lang === 'javascript') return [javascript({ jsx: true, typescript: false })];
    if (lang === 'json') return [jsonLanguage()];
    if (lang === 'css') return [cssLanguage()];
    return [];
  }, [activeFile, resolvePath]);

  const lintExtension = useMemo(
    () =>
      linter((view) => {
        if (!lintDiagnostics.length) return [];
        return lintDiagnostics.map((diag) => {
          const lineInfo = view.state.doc.line(Math.max(1, diag.line));
          const from = lineInfo.from + Math.max(diag.column - 1, 0);
          const to = Math.min(lineInfo.to, from + 1);
          return {
            from,
            to,
            message: diag.message,
            severity: diag.severity as Diagnostic['severity'],
          };
        });
      }),
    [lintDiagnostics]
  );

  return {
    languageExtensions,
    lintExtension,
    lintDiagnostics,
    getLocationFromLog,
  };
}
